import { useEffect, useRef, useState } from "react";
import { Users, X } from "lucide-react";
import { listAssignments } from "../assignments/assignmentsApi.js";
import { listMatchingRecommendations } from "../matchingRecommendation/matchingRecommendationApi.js";

/**
 * P161: "ต้องสามารถกดดูรายชื่อแรงงานที่เข้ามาในตำแหน่งได้" - the people who have
 * actually come into one position.
 *
 * A worker reaches a position through two real, already-built steps, and this
 * screen shows both rather than picking one and calling it "the workers":
 *
 *  1. **มอบหมายแล้ว** - a real dbo.WmxWorkerAssignments row. This person is on
 *     the job.
 *  2. **อนุมัติจับคู่แล้ว** - an approved matching recommendation. A human has
 *     decided this person fits, but no assignment exists yet.
 *
 * Both come from endpoints that already exist and are already scope-filtered
 * server-side, so this adds no backend. It deliberately shows no candidate
 * *suggestions*: those are the Matching Workspace's job, and mixing "people who
 * came into this position" with "people who might" would make the count on this
 * button mean nothing.
 */

const ASSIGNMENT_STATUS_LABEL = {
  Active: "กำลังทำงาน",
  Pending: "รอเริ่มงาน",
  Completed: "จบงานแล้ว",
  Cancelled: "ยกเลิก",
};

const ASSIGNMENT_STATUS_VARIANT = {
  Active: "success",
  Pending: "warning",
  Completed: "neutral",
  Cancelled: "danger",
};

function assignmentStatusLabel(status) {
  return ASSIGNMENT_STATUS_LABEL[status] ?? status ?? "-";
}

function assignmentStatusVariant(status) {
  return ASSIGNMENT_STATUS_VARIANT[status] ?? "neutral";
}

function workerName(row) {
  return (
    row.workerFullNameNative ||
    row.workerFullNameEnglish ||
    row.worker?.fullNameNative ||
    row.worker?.fullNameEnglish ||
    row.workerName ||
    row.workerCode ||
    "-"
  );
}

function describeError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูรายชื่อแรงงานของตำแหน่งนี้";
  }
  return "ไม่สามารถโหลดรายชื่อแรงงานได้ กรุณาลองใหม่อีกครั้ง";
}

export default function JobWorkersDialog({ open, job, onClose }) {
  const dialogRef = useRef(null);
  const [state, setState] = useState({ status: "idle", assignments: [], approvals: [], error: null });

  const jobId = job?.jobId;

  useEffect(() => {
    if (!open || !jobId) {
      setState({ status: "idle", assignments: [], approvals: [], error: null });
      return undefined;
    }

    let cancelled = false;
    setState({ status: "loading", assignments: [], approvals: [], error: null });

    // Both reads are scope-filtered server-side from the session; this screen
    // never sends a scope of its own.
    Promise.all([listAssignments({ jobId, pageSize: 100 }), listMatchingRecommendations({ jobId, pageSize: 100 })])
      .then(([assignmentsData, approvalsData]) => {
        if (cancelled) {
          return;
        }
        setState({
          status: "ready",
          assignments: assignmentsData?.items ?? [],
          approvals: approvalsData?.items ?? [],
          error: null,
        });
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", assignments: [], approvals: [], error });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, jobId]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const assignedWorkerIds = new Set(state.assignments.map((row) => row.workerId));
  // An approved worker who has since been assigned is one person, listed once,
  // under the further-along of the two steps.
  const approvalsNotYetAssigned = state.approvals.filter((row) => !assignedWorkerIds.has(row.workerId));
  const totalPeople = state.assignments.length + approvalsNotYetAssigned.length;

  return (
    <div
      className="wx-tjd-root"
      onMouseDown={(event) => {
        if (dialogRef.current && !dialogRef.current.contains(event.target)) {
          onClose();
        }
      }}
    >
      <div className="wx-tjd-backdrop" aria-hidden="true" />
      <div ref={dialogRef} className="wx-tjd-dialog" role="dialog" aria-modal="true" aria-label={`แรงงานในตำแหน่ง ${job?.jobTitle ?? ""}`}>
        <div className="wx-tjd-head">
          <div className="wx-tjd-head-text">
            <h2>
              <Users size={16} aria-hidden="true" /> แรงงานในตำแหน่ง {job?.jobTitle ?? "-"}
            </h2>
            <p>
              {job?.jobCode ?? "-"}
              {state.status === "ready" ? ` · ${totalPeople.toLocaleString("en-US")} คน` : " · กำลังโหลด..."}
            </p>
          </div>
          <div className="wx-tjd-head-actions">
            <button type="button" className="wx-tjd-close" onClick={onClose} aria-label="ปิด">
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="wx-tjd-body">
          <p className="wx-tjd-note">
            รายชื่อนี้คือคนที่เข้ามาในตำแหน่งนี้แล้วจริง ๆ — ทั้งที่มอบหมายงานแล้ว และที่อนุมัติจับคู่แล้วแต่ยังไม่ได้มอบหมาย ไม่ใช่รายชื่อผู้สมัครที่ระบบแนะนำ
          </p>

          {state.status === "loading" && <p className="wx-tjd-state">กำลังโหลดรายชื่อแรงงาน...</p>}

          {state.status === "error" && (
            <p className="wx-tjd-state wx-tjd-state--error" role="alert">
              {describeError(state.error)}
            </p>
          )}

          {state.status === "ready" && totalPeople === 0 && (
            <p className="wx-tjd-state">ยังไม่มีแรงงานเข้ามาในตำแหน่งนี้</p>
          )}

          {state.status === "ready" && state.assignments.length > 0 && (
            <div className="wx-tjd-block">
              <span>มอบหมายงานแล้ว ({state.assignments.length})</span>
              <ul className="wx-tj-worker-list">
                {state.assignments.map((row) => (
                  <li key={row.assignmentId ?? row.workerId}>
                    <span className="wx-tj-worker-name">
                      <strong>{workerName(row)}</strong>
                      <span>{row.workerCode ?? "-"}</span>
                    </span>
                    <span className={`wx-badge wx-badge--${assignmentStatusVariant(row.assignmentStatus)}`}>
                      {assignmentStatusLabel(row.assignmentStatus)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {state.status === "ready" && approvalsNotYetAssigned.length > 0 && (
            <div className="wx-tjd-block">
              <span>อนุมัติจับคู่แล้ว รอมอบหมายงาน ({approvalsNotYetAssigned.length})</span>
              <ul className="wx-tj-worker-list">
                {approvalsNotYetAssigned.map((row) => (
                  <li key={row.recommendationId ?? row.workerId}>
                    <span className="wx-tj-worker-name">
                      <strong>{workerName(row)}</strong>
                      <span>{row.workerCode ?? "-"}</span>
                    </span>
                    {/* The score the approver actually saw, as recorded at the
                        moment they decided - not recomputed here. */}
                    {row.matchScore != null && <span className="wx-badge wx-badge--info">คะแนน {row.matchScore}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
